import { Message } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import get from 'lodash/get';

import { GROUPS, PREFIX } from '@/service/apiConf';
import { postJSON } from '@/service/request';

import { getComputeResourceConfigurations } from '../compute/api';
import { parseSharedMemorySizeGi } from '../shared-memory/sharedMemoryForm';

/** 会话内 docker-probe 结果缓存，key = sourceUrl + 账密 */
const dockerProbeResultCache = new Map<string, DockerProbeResult>();

/** GPU 上次确认时所选架构，提交时用于判断是否需要重选 */
export const GPU_SELECTED_ARCHITECTURE_FIELD = '_gpuSelectedArchitecture';
/** GPU 上次确认时的可选项指纹；架构变更后列表变化则需重选 */
export const GPU_OPTIONS_FINGERPRINT_FIELD = '_gpuOptionsFingerprint';

export interface DockerProbeAuth {
  username?: string;
  password?: string;
  /** 开启私有镜像拉取认证时传 true（docker-probe registryAuth） */
  insecureTLS?: boolean;
}

export interface DockerProbeRequest {
  sourceUrl: string;
  registryAuth?: DockerProbeAuth;
}

export interface DockerProbeResult {
  accessible?: boolean;
  sizeBytes?: number;
  size?: string;
  architectures?: string[];
  registryHost?: string;
  cpuCompatibility?: string;
  mediaType?: string;
}

export interface ImageRegistryPayload {
  server?: string;
  username?: string;
  password?: string;
}

export const MAAS_NODE_DRAWER_VALIDATE_FIELD_ORDER = [
  'name',
  'replicas',
  'maasInferenceSource',
  'system.image',
  'system.imageKey',
  'system.url',
  'registry.username',
  'registry.password',
  'system.architecture',
  'resource.cpu.request',
  'resource.memory.request',
  'resource.memory',
  'resource.gpu.number',
  '_systemDiskSize',
  '_containerDeploySharedMemory',
  'resource.nics',
];

export const CI_RESOURCE_STEP_VALIDATE_FIELD_ORDER = [
  'name',
  'description',
  'workspace',
  'namespace',
  'basic.image.key',
  'basic.image.url',
  'registry.username',
  'registry.password',
  'systemDiskSize',
  'system.architecture',
  'resource.cpu.request',
  'resource.memory',
  'resource.gpu',
  '_containerDeploySharedMemory',
  'network.nics',
  'resource.network.Bandwidth',
];

export function buildDockerProbeCacheKey(sourceUrl: string, auth?: DockerProbeAuth): string {
  return `${String(sourceUrl ?? '').trim()}\0${auth?.insecureTLS === true ? '1' : '0'}\0${String(auth?.username ?? '').trim()}\0${String(auth?.password ?? '')}`;
}

export function getCachedDockerProbeResult(sourceUrl: string, auth?: DockerProbeAuth): DockerProbeResult | undefined {
  return dockerProbeResultCache.get(buildDockerProbeCacheKey(sourceUrl, auth));
}

export function setCachedDockerProbeResult(sourceUrl: string, auth: DockerProbeAuth | undefined, result: DockerProbeResult) {
  dockerProbeResultCache.set(buildDockerProbeCacheKey(sourceUrl, auth), result);
}

/** 单测 / 切镜像时清空会话缓存 */
export function clearDockerProbeCache() {
  dockerProbeResultCache.clear();
}

/** 从镜像地址解析 registry host（docker-probe 未返回 registryHost 时兜底） */
export function extractRegistryHostFromSourceUrl(sourceUrl: string): string | undefined {
  const trimmed = String(sourceUrl ?? '').trim();
  if (!trimmed) return undefined;
  const withoutScheme = trimmed.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, '');
  const hostPort = withoutScheme.split('/')[0]?.trim();
  return hostPort || undefined;
}

export function resolveRegistryHost(registryHost: string | undefined, sourceUrl: string): string | undefined {
  return String(registryHost ?? '').trim() || extractRegistryHostFromSourceUrl(sourceUrl);
}

/** POST /stack-artifacts/docker-probe（命中会话缓存则跳过请求） */
export async function probeDockerImage(data: DockerProbeRequest): Promise<DockerProbeResult> {
  const sourceUrl = String(data.sourceUrl ?? '').trim();
  const cached = getCachedDockerProbeResult(sourceUrl, data.registryAuth);
  if (cached) return cached;
  const result = await postJSON<DockerProbeResult>(`${PREFIX.vapis_dc_ws(GROUPS.image)}/stack-artifacts/docker-probe`, data);
  if (sourceUrl) {
    setCachedDockerProbeResult(sourceUrl, data.registryAuth, result);
  }
  return result;
}

export function readImageSourceUrl(values: Record<string, unknown>, paths: string[]): string {
  for (const path of paths) {
    const value = String(get(values, path) ?? '').trim();
    if (value) return value;
  }
  return '';
}

/**
 * 从表单读取镜像地址。
 * AppInstance 同时存在 `_inferenceSystem`（JSON 父字段）与 `_inferenceSystem.image` 等扁平子字段时，
 * 仅靠 getFieldsValue + lodash get 往往读不到已写入的 URL，需再查 getFieldValue。
 */
export function readFormImageSourceUrl(form: FormInstance, paths: string[], parentJsonField?: string): string {
  const values = form.getFieldsValue() as Record<string, unknown>;
  const fromValues = readImageSourceUrl(values, paths);
  if (fromValues) return fromValues;

  for (const path of paths) {
    const direct = String(form.getFieldValue(path) ?? '').trim();
    if (direct) return direct;
  }

  if (!parentJsonField) return '';
  const raw = form.getFieldValue(parentJsonField);
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const fromObj = String((raw as Record<string, unknown>).image ?? (raw as Record<string, unknown>).url ?? '').trim();
    if (fromObj) return fromObj;
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      return String(parsed?.image ?? parsed?.url ?? '').trim();
    } catch {
      return '';
    }
  }
  return '';
}

function readRegistryInsecureTlsFromValues(values: Record<string, unknown>, prefix = 'registry'): boolean | undefined {
  const nested = get(values, prefix);
  const raw =
    get(values, `${prefix}.insecureTLS`) ??
    values[`${prefix}.insecureTLS`] ??
    (nested as { insecureTLS?: boolean } | undefined)?.insecureTLS;
  if (raw === true) return true;
  if (raw === false) return false;
  return undefined;
}

export interface ReadRegistryAuthOptions {
  /** 节点抽屉镜像拉取认证开关：关闭或未开启时 probe 仍传 `insecureTLS: false` */
  authSwitch?: boolean;
}

export function readRegistryAuthFromValues(
  values: Record<string, unknown>,
  prefix = 'registry',
  options?: ReadRegistryAuthOptions,
): DockerProbeAuth | undefined {
  const nested = get(values, prefix);
  const username = String(
    get(values, `${prefix}.username`) ?? values[`${prefix}.username`] ?? (nested as ImageRegistryPayload | undefined)?.username ?? '',
  ).trim();
  const password = String(
    get(values, `${prefix}.password`) ?? values[`${prefix}.password`] ?? (nested as ImageRegistryPayload | undefined)?.password ?? '',
  );
  const insecureTLS = readRegistryInsecureTlsFromValues(values, prefix);
  if (options?.authSwitch) {
    if (insecureTLS === true) {
      return {
        insecureTLS: true,
        ...(username ? { username } : {}),
        ...(password ? { password } : {}),
      };
    }
    return { insecureTLS: false };
  }
  if (insecureTLS === true) {
    return {
      insecureTLS: true,
      ...(username ? { username } : {}),
      ...(password ? { password } : {}),
    };
  }
  if (insecureTLS === false) return { insecureTLS: false };
  if (!username && !password) return undefined;
  return {
    ...(username ? { username } : {}),
    ...(password ? { password } : {}),
  };
}

/** 合并表单 store 与 registry 叶子，供 docker-probe 读取账密开关 */
export function readRegistryAuthFromForm(
  form: FormInstance,
  prefix = 'registry',
  options?: ReadRegistryAuthOptions,
): DockerProbeAuth | undefined {
  const values = (form.getFields?.() ?? form.getFieldsValue?.() ?? {}) as Record<string, unknown>;
  return readRegistryAuthFromValues(
    {
      ...values,
      [prefix]: form.getFieldValue(prefix) ?? values[prefix],
      [`${prefix}.server`]: form.getFieldValue(`${prefix}.server`),
      [`${prefix}.username`]: form.getFieldValue(`${prefix}.username`),
      [`${prefix}.password`]: form.getFieldValue(`${prefix}.password`),
      [`${prefix}.insecureTLS`]: form.getFieldValue(`${prefix}.insecureTLS`),
    },
    prefix,
    options,
  );
}

export function buildRegistryPayload(server: string | undefined, auth?: DockerProbeAuth): ImageRegistryPayload | undefined {
  const username = String(auth?.username ?? '').trim();
  const password = String(auth?.password ?? '');
  const host = String(server ?? '').trim();
  if (!host && !username && !password) return undefined;
  return {
    ...(host ? { server: host } : {}),
    ...(username ? { username } : {}),
    ...(password ? { password } : {}),
  };
}

interface GroupWithRegistry {
  registry?: ImageRegistryPayload;
  system?: unknown;
}

/** 从组 system.registry 读取，组级 registry 仅作旧数据兜底 */
export function readGroupSystemRegistry(group?: GroupWithRegistry | null): ImageRegistryPayload | undefined {
  if (!group) return undefined;
  const system =
    group.system && typeof group.system === 'object' && !Array.isArray(group.system)
      ? (group.system as Record<string, unknown>)
      : undefined;
  const fromSystem = system?.registry && typeof system.registry === 'object' ? (system.registry as ImageRegistryPayload) : undefined;
  return mergeRegistryPayload(fromSystem, group.registry);
}

/** 账密只挂在 system.registry，并去掉组级 registry */
export function placeRegistryOnGroupSystem<T extends GroupWithRegistry>(group: T, registry?: ImageRegistryPayload): T {
  const systemRecord =
    group.system && typeof group.system === 'object' && !Array.isArray(group.system)
      ? { ...(group.system as Record<string, unknown>) }
      : {};
  const resolved = registry ?? readGroupSystemRegistry(group);
  if (resolved) systemRecord.registry = resolved;
  else delete systemRecord.registry;
  const { registry: _groupRegistry, ...rest } = group;
  if (!Object.keys(systemRecord).length) {
    const { system: _system, ...withoutSystem } = rest;
    return withoutSystem as T;
  }
  return { ...rest, system: systemRecord } as T;
}

export function applyRegistryToGroups<T extends GroupWithRegistry>(groups: T[] | undefined, registry?: ImageRegistryPayload): T[] {
  if (!groups?.length) return groups ?? [];
  return groups.map((group) => placeRegistryOnGroupSystem(group, registry));
}

/** 多源合并：先到先得（server / username / password 各自补齐），避免不完整节点 JSON 盖掉 probe 的 server */
export function mergeRegistryPayload(...sources: (ImageRegistryPayload | undefined)[]): ImageRegistryPayload | undefined {
  let server = '';
  let username = '';
  let password = '';
  for (const src of sources) {
    if (!src || typeof src !== 'object') continue;
    if (!server) server = String(src.server ?? '').trim();
    if (!username) username = String(src.username ?? '').trim();
    if (!password) password = String(src.password ?? '');
  }
  return buildRegistryPayload(server, username || password ? { username, password } : undefined);
}

/** 同时兼容 registry 对象与 registry.server 叶子（含扁平 key） */
export function readRegistryPayloadFromValues(values: Record<string, unknown>, prefix = 'registry'): ImageRegistryPayload | undefined {
  const nested = get(values, prefix) ?? values[prefix];
  const serverFromNested = nested && typeof nested === 'object' ? String((nested as ImageRegistryPayload).server ?? '').trim() : '';
  const server = String(get(values, `${prefix}.server`) ?? values[`${prefix}.server`] ?? '').trim() || serverFromNested;
  return buildRegistryPayload(server, readRegistryAuthFromValues(values, prefix));
}

/** 从表单 store + 叶子读取 registry，避免 getFieldsValue 漏未挂载字段 */
export function readRegistryPayloadFromForm(form: FormInstance | undefined, prefix = 'registry'): ImageRegistryPayload | undefined {
  if (!form) return undefined;
  const values = (form.getFields?.() ?? form.getFieldsValue?.() ?? {}) as Record<string, unknown>;
  return readRegistryPayloadFromValues(
    {
      ...values,
      [prefix]: form.getFieldValue(prefix) ?? values[prefix],
      [`${prefix}.server`]: form.getFieldValue(`${prefix}.server`),
      [`${prefix}.username`]: form.getFieldValue(`${prefix}.username`),
      [`${prefix}.password`]: form.getFieldValue(`${prefix}.password`),
    },
    prefix,
  );
}

/** 按叶子写入 registry，避免整对象覆盖打乱已挂载 Form.Item */
export function applyRegistryLeavesToForm(form: FormInstance, registry: ImageRegistryPayload | undefined, prefix = 'registry') {
  if (!registry) return;
  if (registry.server) form.setFieldValue(`${prefix}.server`, registry.server);
  if (registry.username) form.setFieldValue(`${prefix}.username`, registry.username);
  if (registry.password) form.setFieldValue(`${prefix}.password`, registry.password);
}

function parseGroupedNodesJson(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) {
    return raw.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object');
  }
  if (typeof raw !== 'string' || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object');
  } catch (error) {
    console.log(error);
    return [];
  }
}

/** 叶子 + 节点 JSON 同步同一份 registry，避免 assemble 时用不完整组内 registry 盖掉 server */
export function applyRegistryToGroupedNodeFields(form: FormInstance, registry?: ImageRegistryPayload) {
  applyRegistryLeavesToForm(form, registry);
  if (!registry) return;
  const modules = form.getFieldValue('skuResourceModules') as { gatewayConfig?: boolean } | undefined;
  const nodeFields = ['masterNodes', 'workerNodes', ...(modules?.gatewayConfig === true ? ['gatewayNodes'] : [])];
  for (const field of nodeFields) {
    const nodes = parseGroupedNodesJson(form.getFieldValue(field));
    if (!nodes.length) continue;
    form.setFieldValue(field, JSON.stringify(applyRegistryToGroups(nodes, registry)));
  }
}

/** 业务校验失败：必须 toast；可见 Form.Item 再写字段错误（隐藏 persist 不会显示 help） */
export function setFormFieldError(form: FormInstance, field: string | undefined, message: string) {
  const text = String(message ?? '').trim();
  if (text) Message.error(text);
  if (!field || !text) return;
  form.setFields({
    [field]: { error: { message: text } },
  });
}

export interface ImageConfirmFieldPaths {
  sourceUrl?: string;
  architecture?: string;
  cpu?: string;
  memory?: string;
  sharedMemory?: string;
  gpu?: string;
}

export const MAAS_IMAGE_CONFIRM_FIELDS: Required<ImageConfirmFieldPaths> = {
  sourceUrl: 'system.image',
  architecture: 'system.architecture',
  cpu: 'resource.cpu.request',
  memory: 'resource.memory.request',
  sharedMemory: '_containerDeploySharedMemory',
  gpu: 'resource.gpu.number',
};

export const CI_IMAGE_CONFIRM_FIELDS: Required<ImageConfirmFieldPaths> = {
  sourceUrl: 'basic.image.url',
  architecture: 'system.architecture',
  cpu: 'resource.cpu.request',
  memory: 'resource.memory',
  sharedMemory: '_containerDeploySharedMemory',
  gpu: 'resource.gpu.number',
};

export function pickFirstFormErrorByFieldOrder(error: unknown, fieldOrder: string[] = []): string | undefined {
  const errors = (error as { errors?: Record<string, { message?: string } | string> })?.errors;
  if (!errors || typeof errors !== 'object') return undefined;

  const readMessage = (field: string): string | undefined => {
    const item = errors[field];
    if (!item) return undefined;
    if (typeof item === 'string') return item.trim() || undefined;
    return String(item.message ?? '').trim() || undefined;
  };

  for (const field of fieldOrder) {
    const message = readMessage(field);
    if (message) return message;
  }
  for (const field of Object.keys(errors)) {
    if (field === 'errors' || field === 'message' || field === 'stack' || field === 'name') continue;
    const message = readMessage(field);
    if (message) return message;
  }
  return undefined;
}

export interface ProbeImageArchitectureOptions {
  sourceUrl: string;
  registryAuth?: DockerProbeAuth;
  architecture?: string;
  /** 应用开抽屉前只探可达性，不校验架构 */
  requireArchitecture?: boolean;
  sourceUrlField?: string;
  architectureField?: string;
  /** 未填写镜像地址时的提示；默认请输入镜像 */
  emptySourceMessage?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}

export type ProbeImageArchitectureResult =
  | { ok: true; registryHost?: string; architectures: string[] }
  | { ok: false; message: string; field?: string };

/** 调用 docker-probe，校验镜像可访问与架构范围 */
export async function probeImageAndValidateArchitecture(options: ProbeImageArchitectureOptions): Promise<ProbeImageArchitectureResult> {
  const sourceUrl = String(options.sourceUrl ?? '').trim();
  const sourceUrlField = options.sourceUrlField || MAAS_IMAGE_CONFIRM_FIELDS.sourceUrl;
  if (!sourceUrl) {
    return {
      ok: false,
      message: options.emptySourceMessage || options.t('data-center:please.enter.image'),
      field: sourceUrlField,
    };
  }

  let result: DockerProbeResult;
  try {
    result = await probeDockerImage({
      sourceUrl,
      ...(options.registryAuth ? { registryAuth: options.registryAuth } : {}),
    });
  } catch (error) {
    console.log(error);
    return { ok: false, message: options.t('data-center:image.address.invalid'), field: sourceUrlField };
  }

  if (result?.accessible === false) {
    return { ok: false, message: options.t('data-center:image.address.invalid'), field: sourceUrlField };
  }

  const architectureField = options.architectureField || MAAS_IMAGE_CONFIRM_FIELDS.architecture;
  const architecture = String(options.architecture ?? '').trim();
  if (options.requireArchitecture !== false && !architecture) {
    return { ok: false, message: options.t('data-center:please.select.architecture'), field: architectureField };
  }

  const architectures = (result?.architectures || []).map((item) => String(item ?? '').trim()).filter(Boolean);
  if (options.requireArchitecture !== false && architecture && architectures.length && !architectures.includes(architecture)) {
    return {
      ok: false,
      message: options.t('data-center:architecture.not.supported.by.image', { architectures: architectures.join(' / ') }),
      field: architectureField,
    };
  }

  return {
    ok: true,
    registryHost: resolveRegistryHost(result?.registryHost, sourceUrl),
    architectures,
  };
}

export function validateBuiltinComputeMins(options: {
  minCpuCores?: number;
  minMemoryGi?: number;
  minSharedMemoryGi?: number;
  cpu?: number;
  memoryGi?: number;
  sharedMemoryGi?: number;
  t: (key: string, options?: Record<string, unknown>) => string;
}): { field: 'cpu' | 'memory' | 'sharedMemory'; message: string } | undefined {
  const { minCpuCores, minMemoryGi, minSharedMemoryGi, cpu, memoryGi, sharedMemoryGi, t } = options;
  if (minCpuCores && minCpuCores > 0 && (cpu == null || cpu < minCpuCores)) {
    return { field: 'cpu', message: t('data-center:cpu.min.value', { min: minCpuCores }) };
  }
  if (minMemoryGi && minMemoryGi > 0 && (memoryGi == null || memoryGi < minMemoryGi)) {
    return { field: 'memory', message: t('data-center:memory.min.value', { min: minMemoryGi }) };
  }
  if (minSharedMemoryGi && minSharedMemoryGi > 0) {
    if (sharedMemoryGi == null) {
      return { field: 'sharedMemory', message: t('cluster:required', { content: t('data-center:shared.memory') }) };
    }
    if (sharedMemoryGi < minSharedMemoryGi) {
      return { field: 'sharedMemory', message: t('data-center:shared.memory.min.value', { min: minSharedMemoryGi }) };
    }
  }
  return undefined;
}

export function readSharedMemoryGiFromFormValue(raw: unknown): number | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const sizeLimit = String((raw as { sizeLimit?: string }).sizeLimit ?? '').trim();
  if (!sizeLimit) return undefined;
  return parseSharedMemorySizeGi(sizeLimit);
}

/** 与 GPU 下拉一致：无 architecture 字段的库存项对所有架构可见 */
export function filterGpuInventoryByArchitecture<T extends { architecture?: string }>(list: T[] | undefined, architecture: string): T[] {
  const arch = String(architecture ?? '').trim();
  const items = Array.isArray(list) ? list : [];
  if (!arch) return items;
  return items.filter((item) => !item.architecture || String(item.architecture) === arch);
}

/** GPU 可选项指纹：vendorID:deviceID，缺 ID 时用 vendor:device */
export function fingerprintGpuOptions(list: unknown[]): string {
  const keys = (Array.isArray(list) ? list : [])
    .map((raw) => {
      const item = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
      const vendorID = String(item.vendorID ?? '')
        .trim()
        .toUpperCase();
      const deviceID = String(item.deviceID ?? '')
        .trim()
        .toUpperCase();
      if (vendorID && deviceID) return `${vendorID}:${deviceID}`;
      const vendor = String(item.vendor ?? '').trim();
      const device = String(item.device ?? '').trim();
      if (vendor && device) return `${vendor}:${device}`;
      return '';
    })
    .filter(Boolean);
  return [...new Set(keys)].sort().join('|');
}

export async function fetchGpuOptionsFingerprint(architecture: string): Promise<string | undefined> {
  const inventory = await fetchGpuInventoryForArchitecture(architecture);
  if (!inventory) return undefined;
  return fingerprintGpuOptions(inventory);
}

/** 拉取当前架构下的 GPU 库存列表，供弹窗确认库存包含判断 */
export async function fetchGpuInventoryForArchitecture(architecture: string): Promise<Record<string, unknown>[] | undefined> {
  const arch = String(architecture ?? '').trim();
  if (!arch) return undefined;
  const list = (await getComputeResourceConfigurations({
    workloadType: 'ContainerInstance',
    resourceType: 'vGPU',
    architecture: arch,
  })) as Record<string, unknown>[];
  return filterGpuInventoryByArchitecture(Array.isArray(list) ? list : [], arch);
}

/** GPU 弹窗确定 / 抽屉回填后盖章当前架构可选项指纹 */
export async function stampGpuOptionsFingerprint(form: FormInstance, architecture: string): Promise<void> {
  const arch = String(architecture ?? '').trim();
  if (!arch) return;
  try {
    const fingerprint = await fetchGpuOptionsFingerprint(arch);
    if (fingerprint) form.setFieldValue(GPU_OPTIONS_FINGERPRINT_FIELD, fingerprint);
  } catch (error) {
    console.log(error);
  }
}

function hasGpuSelection(gpu: unknown): boolean {
  return Boolean(gpuIdentityKey(gpu));
}

/** GPU 选型身份（不含数量）：选项列表变化后原样确定不算重选 */
export function gpuIdentityKey(gpu: unknown): string {
  if (!gpu || typeof gpu !== 'object') return '';
  const record = gpu as Record<string, unknown>;
  const vendorID = String(record.vendorID ?? '')
    .trim()
    .toUpperCase();
  const deviceID = String(record.deviceID ?? '')
    .trim()
    .toUpperCase();
  const vendor = String(record.vendor ?? '').trim();
  const device = String(record.device ?? '').trim();
  return [vendorID && deviceID ? `${vendorID}:${deviceID}` : '', vendor && device ? `${vendor}:${device}` : ''].filter(Boolean).join('|');
}

/** 当前 GPU 是否仍落在给定库存列表内 */
export function isGpuInInventory(gpu: unknown, list: unknown[]): boolean {
  if (!hasGpuSelection(gpu)) return false;
  const record = gpu as Record<string, unknown>;
  const vendorID = String(record.vendorID ?? '')
    .trim()
    .toUpperCase();
  const deviceID = String(record.deviceID ?? '')
    .trim()
    .toUpperCase();
  const vendor = String(record.vendor ?? '').trim();
  const device = String(record.device ?? '').trim();
  return (Array.isArray(list) ? list : []).some((raw) => {
    const item = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const itemVendorID = String(item.vendorID ?? '')
      .trim()
      .toUpperCase();
    const itemDeviceID = String(item.deviceID ?? '')
      .trim()
      .toUpperCase();
    if (vendorID && deviceID) {
      return itemVendorID === vendorID && itemDeviceID === deviceID;
    }
    return Boolean(vendor && device) && String(item.vendor ?? '').trim() === vendor && String(item.device ?? '').trim() === device;
  });
}

/**
 * 弹窗确定时是否盖章可选项指纹。
 * 指纹变化但所选 GPU 仍在当前库存内时仍允许盖章（SKU/下单计价列表常不随架构变，不应强迫移除重选）。
 */
export function shouldStampGpuOptionsOnModalConfirm(options: {
  previousGpu?: unknown;
  nextGpu?: unknown;
  stampedFingerprint?: string;
  currentFingerprint?: string;
  /** 当前架构库存；传入时以「仍在列表内」为准 */
  currentInventory?: unknown[];
}): boolean {
  const stamped = String(options.stampedFingerprint ?? '').trim();
  const current = String(options.currentFingerprint ?? '').trim();
  if (!stamped || !current || stamped === current) return true;
  if (options.currentInventory != null) {
    if (!options.currentInventory.length) return true;
    return isGpuInInventory(options.nextGpu, options.currentInventory);
  }
  return gpuIdentityKey(options.previousGpu) !== gpuIdentityKey(options.nextGpu);
}

/** 提交时：所选 GPU 必须属于当前架构库存；指纹仅作参考，不单独拦截 */
export async function validateGpuAgainstArchitecture(options: {
  gpu?: unknown;
  architecture?: string;
  selectedGpuOptionsFingerprint?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}): Promise<string | undefined> {
  if (!hasGpuSelection(options.gpu)) return undefined;
  const architecture = String(options.architecture ?? '').trim();
  if (!architecture) return options.t('data-center:please.select.architecture');

  try {
    const list = (await getComputeResourceConfigurations({
      workloadType: 'ContainerInstance',
      resourceType: 'vGPU',
      architecture,
    })) as Record<string, unknown>[];
    const visible = filterGpuInventoryByArchitecture(Array.isArray(list) ? list : [], architecture);
    const currentFingerprint = fingerprintGpuOptions(visible);
    const stampedFingerprint = String(options.selectedGpuOptionsFingerprint ?? '').trim();
    /** 指纹一致可跳过逐项比对；不一致时仍以库存包含为准 */
    if (stampedFingerprint && stampedFingerprint === currentFingerprint) return undefined;
    if (!isGpuInInventory(options.gpu, visible)) {
      return options.t('data-center:gpu.reselect.after.architecture.change');
    }
  } catch (error) {
    console.log(error);
    return options.t('data-center:gpu.not.match.architecture');
  }
  return undefined;
}

/** 从镜像 containerDeployConfig.sharedMemory.sizeLimit 读取共享内存最小值（Gi） */
export function readMinSharedMemoryGiFromDeployConfig(config?: Record<string, unknown> | null): number | undefined {
  const sharedMemory = config?.sharedMemory as { sizeLimit?: string } | undefined;
  const sizeLimit = String(sharedMemory?.sizeLimit ?? '').trim();
  if (!sizeLimit) return undefined;
  const gi = parseSharedMemorySizeGi(sizeLimit, 0);
  return gi > 0 ? gi : undefined;
}

export interface ImageConfirmBusinessCheckOptions {
  sourceUrl: string;
  registryAuth?: DockerProbeAuth;
  architecture?: string;
  gpu?: unknown;
  selectedGpuArchitecture?: string;
  selectedGpuOptionsFingerprint?: string;
  isBuiltin?: boolean;
  minCpuCores?: number;
  minMemoryGi?: number;
  minSharedMemoryGi?: number;
  cpu?: number;
  memoryGi?: number;
  sharedMemoryGi?: number;
  fields?: ImageConfirmFieldPaths;
  emptySourceMessage?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}

export type ImageConfirmBusinessCheckResult = { ok: true; registryHost?: string } | { ok: false; message: string; field?: string };

/** 提交时：docker-probe → 架构范围 → 内置下限 / 当前架构 GPU 可选项 */
export async function runImageConfirmBusinessChecks(options: ImageConfirmBusinessCheckOptions): Promise<ImageConfirmBusinessCheckResult> {
  const fields = { ...MAAS_IMAGE_CONFIRM_FIELDS, ...options.fields };
  const probed = await probeImageAndValidateArchitecture({
    sourceUrl: options.sourceUrl,
    registryAuth: options.registryAuth,
    architecture: options.architecture,
    sourceUrlField: fields.sourceUrl,
    architectureField: fields.architecture,
    emptySourceMessage: options.emptySourceMessage,
    t: options.t,
  });
  if (!probed.ok) return probed;

  if (options.isBuiltin) {
    const minError = validateBuiltinComputeMins({
      minCpuCores: options.minCpuCores,
      minMemoryGi: options.minMemoryGi,
      minSharedMemoryGi: options.minSharedMemoryGi,
      cpu: options.cpu,
      memoryGi: options.memoryGi,
      sharedMemoryGi: options.sharedMemoryGi,
      t: options.t,
    });
    if (minError) {
      return { ok: false, message: minError.message, field: fields[minError.field] };
    }
  }

  const gpuMessage = await validateGpuAgainstArchitecture({
    gpu: options.gpu,
    architecture: options.architecture,
    selectedGpuOptionsFingerprint: options.selectedGpuOptionsFingerprint,
    t: options.t,
  });
  if (gpuMessage) return { ok: false, message: gpuMessage, field: fields.gpu };

  return { ok: true, registryHost: probed.registryHost };
}

export interface EnsureImageRegistryOnFormOptions {
  sourceUrl: string;
  sourceUrlField?: string;
  emptySourceMessage?: string;
  requireArchitecture?: boolean;
  architecture?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/** 下一步 / 提交：probe（命中缓存则跳过请求）并把 registry.server 写入叶子 */
export async function ensureImageRegistryOnForm(
  form: FormInstance,
  options: EnsureImageRegistryOnFormOptions,
): Promise<{ ok: true; registry?: ImageRegistryPayload } | { ok: false; message: string; field?: string }> {
  const values = form.getFieldsValue() as Record<string, unknown>;
  const registryAuth = readRegistryAuthFromForm(form);
  const probed = await probeImageAndValidateArchitecture({
    sourceUrl: options.sourceUrl,
    registryAuth,
    architecture: options.architecture ?? String(form.getFieldValue('system.architecture') ?? '').trim(),
    requireArchitecture: options.requireArchitecture ?? false,
    sourceUrlField: options.sourceUrlField,
    emptySourceMessage: options.emptySourceMessage,
    t: options.t,
  });
  if (probed.ok === false) {
    setFormFieldError(form, probed.field, probed.message);
    return probed;
  }
  const registry = buildRegistryPayload(resolveRegistryHost(probed.registryHost, options.sourceUrl), registryAuth);
  applyRegistryToGroupedNodeFields(form, registry);
  return { ok: true, registry };
}
